import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Brain, Database, BarChart3, Loader2 } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { globalSearch, flattenSearchResults, type SearchResult, type GroupedSearchResults } from '@/services/search'

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const RESULT_TYPE_CONFIG = {
  model: {
    label: 'Models',
    icon: Brain,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  data_source: {
    label: 'Data Sources',
    icon: Database,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  prediction: {
    label: 'Predictions',
    icon: BarChart3,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
} as const

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GroupedSearchResults>({
    models: [],
    dataSources: [],
    predictions: [],
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Flatten results for keyboard navigation
  const flatResults = flattenSearchResults(results)
  const totalResults = flatResults.length

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0)
      setQuery('')
      setResults({ models: [], dataSources: [], predictions: [] })
      setSelectedIndex(0)
    }
  }, [open])

  // Perform search with debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults({ models: [], dataSources: [], predictions: [] })
      setSelectedIndex(0)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const timeoutId = setTimeout(async () => {
      try {
        const searchResults = await globalSearch(query)
        setResults(searchResults)
        setSelectedIndex(0)
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && totalResults > 0) {
      const selectedElement = resultsRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex, totalResults])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (totalResults === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % totalResults)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults)
          break
        case 'Enter':
          e.preventDefault()
          if (flatResults[selectedIndex]) {
            handleSelectResult(flatResults[selectedIndex])
          }
          break
        case 'Escape':
          onOpenChange(false)
          break
      }
    },
    [totalResults, flatResults, selectedIndex, onOpenChange]
  )

  const handleSelectResult = (result: SearchResult) => {
    navigate(result.url)
    onOpenChange(false)
  }

  const renderResultGroup = (
    title: string,
    items: SearchResult[],
    startIndex: number
  ) => {
    if (items.length === 0) return null

    const config = RESULT_TYPE_CONFIG[items[0].type]
    const Icon = config.icon

    return (
      <div key={title} className="mb-4">
        <div className="mb-2 flex items-center gap-2 px-3">
          <Icon className={cn('h-4 w-4', config.color)} />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </h3>
          <Badge variant="secondary" className="ml-auto text-xs">
            {items.length}
          </Badge>
        </div>
        <div className="space-y-1">
          {items.map((result, index) => {
            const globalIndex = startIndex + index
            const Icon = RESULT_TYPE_CONFIG[result.type].icon
            const config = RESULT_TYPE_CONFIG[result.type]
            // Cap animation delay at 10 items (800ms max) to avoid slow rendering
            const animationDelay = Math.min(globalIndex, 10) * 80

            return (
              <button
                key={result.id}
                data-index={globalIndex}
                onClick={() => handleSelectResult(result)}
                className={cn(
                  'w-full rounded-lg px-3 py-2.5 text-left transition-colors',
                  'flex items-start gap-3',
                  'hover:bg-accent',
                  'animate-fade-in-up opacity-0 [animation-fill-mode:forwards]',
                  selectedIndex === globalIndex && 'bg-accent'
                )}
                style={{ animationDelay: `${animationDelay}ms` }}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                    config.bgColor
                  )}
                >
                  <Icon className={cn('h-5 w-5', config.color)} />
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="truncate text-sm font-medium">{result.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {result.description}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        {/* Search Input */}
        <div className="flex items-center border-b px-4">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search models, data sources, predictions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-0 shadow-none focus-visible:ring-0 h-12"
            autoComplete="off"
          />
          {isSearching && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Search Results */}
        <div
          ref={resultsRef}
          className="max-h-[400px] overflow-y-auto p-4"
        >
          {!query.trim() && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm font-medium text-muted-foreground">
                Search for models, data sources, or predictions
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Use <kbd className="px-1.5 py-0.5 text-xs font-semibold border rounded">Cmd+K</kbd> to open search anywhere
              </p>
            </div>
          )}

          {query.trim() && totalResults === 0 && !isSearching && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm font-medium text-muted-foreground">
                No results found for "{query}"
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Try searching with different keywords
              </p>
            </div>
          )}

          {query.trim() && totalResults > 0 && (
            <div key={query}>
              {renderResultGroup('Models', results.models, 0)}
              {renderResultGroup(
                'Data Sources',
                results.dataSources,
                results.models.length
              )}
              {renderResultGroup(
                'Predictions',
                results.predictions,
                results.models.length + results.dataSources.length
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {totalResults > 0 && (
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span>
                <kbd className="px-1.5 py-0.5 font-semibold border rounded">↑↓</kbd> Navigate
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 font-semibold border rounded">Enter</kbd> Select
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 font-semibold border rounded">Esc</kbd> Close
              </span>
            </div>
            <span>{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
